import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Input, DateInput, Select } from "../../components/ui";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import SelectImage from "../../components/ui/SelectImage";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useGetAccountDetails from "../../hooks/MyAccount/useGetAccountDetails";
import useUpdateAccount from "../../hooks/MyAccount/useUpdateAccount";
import Spinner from "../../components/loader/Spinner";
import InlineLoader from "../../components/loader/InlineLoader";

const EditAccount = () => {
  const [profileImageUrl, setProfileImageUrl] = useState(null);

  // Store the ID of the image being removed
  const [removingImageId, setRemovingImageId] = useState(null);

  // Breadcrumbs
  const breadcrumbs = [
    { label: 'My Account', link: '/my-account' },
    { label: 'Edit Profile', link: '' }
  ];

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      username: "",
      name: "",
      email: "",
      phone: "",
      date_of_birth: null,
      gender: "",
      designation: "",
      department: "",
      p_address_1: "",
      p_address_2: "",
      p_pincode: "",
      p_city: "",
      p_state: "",
      aadhar: "",
      pan: "",
      user_profile_image_id: "",
      profile_image: "",
    },
  });

  // Fetch user data
  const { list } = useGetAccountDetails({
    onSuccess: (response) => {
      if (response?.data) {
        const userData = response.data;

        // Handle date of birth - API returns YYYY-MM-DD format
        let dateOfBirth = null;
        if (userData.date_of_birth && userData.date_of_birth !== "0000-00-00") {
          dateOfBirth = new Date(userData.date_of_birth);

          // Additional validation to ensure the date is valid
          if (isNaN(dateOfBirth.getTime())) {
            console.error(
              "Invalid date format received:",
              userData.date_of_birth
            );
            dateOfBirth = null;
          }
        }

        // Only set profile image if URL exists and is not empty
        if (userData.profile_image_url && userData.profile_image_url !== "") {
          setProfileImageUrl(userData.profile_image_url);
        } else {
          setProfileImageUrl(null);
        }

        // Update form with user data
        reset({
          username: userData.username || "",
          name: userData.name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          date_of_birth: dateOfBirth,
          gender: userData.gender || "",
          designation: userData.designation || "",
          department: userData.department || "",
          p_address_1: userData.p_address_1 || "",
          p_address_2: userData.p_address_2 || "",
          aadhar: userData.aadhar || "",
          pan: userData.pan || "",
          user_profile_image_id: userData.user_profile_image_id || "",
          profile_image: userData.profile_image_url
            ? {
                image_id: userData.user_profile_image_id || "",
                image_url: userData.profile_image_url || "",
              }
            : "",
        });
      }
    },
    onError: (error) => {
      toast.error("Failed to load profile data");
    },
  });

  const navigate = useNavigate();

  // Update account details with loading states
  const { updateAccount, removeProfileImage, isUpdating, isRemoving } =
    useUpdateAccount({
      onSuccess: (response, isImageRemoval = false) => {
        if (isImageRemoval) {
          // If removing image, just update the form and state
          if (removingImageId) {
            // Reset the image fields in the form
            const formValues = { ...control._formValues };
            formValues.profile_image = "";
            formValues.user_profile_image_id = "";

            // Update the form
            reset(formValues);

            // Clear the profile image URL
            setProfileImageUrl(null);

            // Clear the tracking state
            setRemovingImageId(null);

            // Show success message for image removal
            toast.success("Profile picture removed successfully");
          }
        } else {
          // For regular profile updates, show success and navigate
          toast.success("Profile updated successfully");
          navigate("/my-account");
        }
      },
      onError: (error) => {
        setRemovingImageId(null);
        toast.error(
          isRemoving ? "Failed to remove profile picture" : "Failed to update profile"
        );
      },
    });

  const onSubmit = (data) => {
    // Create payload with required fields only
    const payload = {
      name: data.name,
      phone: data.phone,
      gender: data.gender,
    };
    // Include profile image if it exists
    if (data.profile_image && data.profile_image.image_id) {
      payload.user_profile_image_id = data.profile_image.image_id;
    }

    // Handle date of birth - convert to YYYY-MM-DD format
    if (data.date_of_birth) {
      // Format as YYYY-MM-DD using toISOString and taking just the date part
      payload.date_of_birth = data.date_of_birth.toISOString().split("T")[0];
    }

    console.log("Form data to submit:", payload);
    updateAccount(payload);
  };

    // Handle image removal
  const handleImageRemove = (imageId) => {
    setRemovingImageId(imageId);
    removeProfileImage(imageId);
  };

  const handleErrors = (errors) => {
    console.log(errors);
    toast.error("Please check the form for errors");
  };
  if (list.isLoading || list.isFetching) {
    return <Spinner fullWidth={true} />;
  }

  return (
    <div className="p-2 sm:p-4 mb-10">

      {/* Breadcrumbs */}
      <BreadCrumbs crumbs={breadcrumbs} />

      <div className="flex justify-between items-center my-3 sm:my-5">
        <h1 className="text-xl sm:text-2xl font-semibold">Edit Profile</h1>
      </div>

      {!list.isLoading && (
        <form onSubmit={handleSubmit(onSubmit, handleErrors)}>
          {/* Profile Image Section */}
          <div className="border p-5 rounded-lg shadow-md mb-6">
            <div className="flex flex-col items-center">
              <SelectImage
                control={control}
                name="profile_image"
                initialPreview={profileImageUrl}
                label="Upload Profile Picture"
                className="w-full"
                onRemove={handleImageRemove}
                isRemoving={isRemoving} 
              />
            </div>
          </div>

          {/* Personal Information Section */}
          <div className="border p-5 rounded-lg shadow-md mb-6">
            <h1 className="text-lg sm:text-xl font-semibold mb-4">
              Personal Information
            </h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Input
                control={control}
                label="Username"
                name="username"
                placeholder="Username"
                disabled={true}
              />

              <Input
                control={control}
                label="Name"
                name="name"
                placeholder="Please enter first name"
                rules={{
                  required: "First name is required",
                }}
              />

              <Input
                control={control}
                label="Email"
                name="email"
                placeholder="Please enter email"
                disabled={true}
              />

              <Input
                control={control}
                label="Phone"
                name="phone"
                placeholder="Please enter phone number"
                rules={{
                  required: "Phone number is required",
                  maxLength: {
                    value: 10,
                    message: "Phone number should have at most 10 digits",
                  },
                  minLength: {
                    value: 10,
                    message: "Phone number should have at least 10 digits",
                  },
                  validate: {
                    matchPattern: (v) =>
                      /^(0|[1-9]\d*)(\.\d+)?$/.test(v) ||
                      "Phone number must be valid",
                  },
                }}
              />

              <DateInput
                control={control}
                label="Date of Birth"
                name="date_of_birth"
                dateFormat="dd/MM/yyyy"
                rules={{
                  required: "Date of Birth is required",
                  validate: (value) =>
                    !isNaN(new Date(value)) || "Invalid date format",
                }}
              />

              <Select
                control={control}
                label="Gender"
                name="gender"
                placeholder="Please select gender"
                options={[
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                  { value: "other", label: "Other" },
                ]}
                rules={{
                  required: "Gender is required",
                }}
              />

              <Input
                control={control}
                label="Designation"
                name="designation"
                placeholder="Please enter designation"
                disabled={true}
              />

              <Input
                control={control}
                label="Department"
                name="department"
                placeholder="Please enter department"
                disabled={true}
              />
            </div>
          </div>

          {/* Address Section */}
          <div className="border p-5 rounded-lg shadow-md mb-6">
            <h1 className="text-lg sm:text-xl font-semibold mb-4">Address</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Input
                control={control}
                label="Line 1"
                name="p_address_1"
                placeholder="Please enter address line 1"
                disabled={true}
              />

              <Input
                control={control}
                label="Line 2"
                name="p_address_2"
                placeholder="Please enter address line 2"
                disabled={true}
              />
            </div>
          </div>

          {/* Identity Information Section */}
          <div className="border p-5 rounded-lg shadow-md mb-6">
            <h1 className="text-lg sm:text-xl font-semibold mb-4">
              Identity Information
            </h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Input
                control={control}
                label="Aadhar Number"
                name="aadhar"
                placeholder="Please enter 12-digit Aadhar number"
                disabled={true}
              />

              <Input
                control={control}
                label="PAN Number"
                name="pan"
                placeholder="Please enter PAN number"
                disabled={true}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/my-account")}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <InlineLoader className="mr-2" /> Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EditAccount;
